import { NavLink, useLocation } from "react-router-dom";
import { useContext, useState, useRef } from "react";
import { createPortal } from "react-dom";
import { PanelLeftClose, PanelLeftOpen } from "lucide-react";
import { AuthContext } from "../../context/AuthContext";
import { isFreelance } from "../../utils/constants/accountType";
import {
  FiHome,
  FiSettings,
  FiChevronDown,
  FiUsers,
  FiBriefcase,
  FiBarChart2,
  FiFileText,
  FiDollarSign,
  FiCreditCard,
  FiPackage,
  FiFolder,
  FiCheckSquare,
} from "react-icons/fi";

const COLLAPSED_W = 72; // px — matches w-[4.5rem]

const Sidebar = ({ isOpen, setIsOpen }) => {
  const { user } = useContext(AuthContext);
  const location = useLocation();
  const isVendor = user?.roleSlug === "vendor";
  const isVendorManager = user?.roleSlug === "vendor_manager";
  const isProjectManager = user?.roleSlug === "project_manager";
  const freelance = isFreelance(user);
  const isAdminSetupIncomplete =
    user?.roleSlug === "administrator" && !user?.setup_completed;

  const [expandedCategories, setExpandedCategories] = useState(() => {
    try {
      const saved = localStorage.getItem("sidebar-expanded");
      return saved ? new Set(JSON.parse(saved)) : new Set();
    } catch {
      return new Set();
    }
  });
  const [flyout, setFlyout] = useState(null);
  const flyoutTimer = useRef(null);

  const financesVisible = user?.vendor_flags?.finances_visible ?? false;

  const vendorLinks = [
    { to: "/dashboard", icon: FiBriefcase, label: "Dashboard" },
    { to: "/jobs",      icon: FiPackage,   label: "Jobs"      },
    ...(financesVisible
      ? [
          { to: "/vendor/receivables", icon: FiDollarSign, label: "Receivables" },
          { to: "/vendor/invoices",    icon: FiFileText,   label: "Invoices"    },
          { to: "/vendor/payments",    icon: FiCreditCard, label: "Payments"    },
        ]
      : []),
  ];

  const adminLinks = [
    { label: "Dashboard",     icon: FiHome,     to: "/",              hasSubcategories: false },
    {
      label: "System Values", icon: FiBarChart2, to: "/system-values", hasSubcategories: true,
      subcategories: [
        { label: "Manager",         to: "/managers" },
        { label: "Client Pool",     to: "/client-pools" },
        { label: "Services",        to: "/system-values/services" },
        { label: "Language Pairs",  to: "/system-values/language-pairs" },
        { label: "Specializations", to: "/system-values/specializations" },
        { label: "Units",           to: "/system-values/units" },
        { label: "Currencies",      to: "/system-values/currencies" },
        { label: "Payment Methods", to: "/system-values/payment-methods" },
      ],
    },
    {
      label: "Vendor", icon: FiUsers, hasSubcategories: true,
      subcategories: [
        { label: "Vendor",             to: "/vendors", end: true     },
        { label: "Create New",         to: "/vendors/create-vendor"  },
        { label: "Vendor Application", to: "/vendor/applications"    },
        { label: "Payables",           to: "/vendor/payables"        },
        { label: "Invoice",            to: "/vendor/invoices"        },
        { label: "Payment",            to: "/vendor/payments"        },
      ],
    },
    {
      label: "Client", icon: FiUsers, hasSubcategories: true,
      subcategories: [
        { label: "Client",      to: "/clients", end: true   },
        { label: "Create New",  to: "/clients/create-client" },
        { label: "Receivables", to: "/client/receivables"    },
        { label: "Invoice",     to: "/client/invoices"       },
        { label: "Payment",     to: "/client/payments"       },
      ],
    },
    { label: "Project", icon: FiFolder,  to: "/projects", hasSubcategories: false },
    { label: "Jobs",    icon: FiPackage, to: "/jobs",     hasSubcategories: false  },
    { label: "Files",   icon: FiFileText, to: "/files",   hasSubcategories: false  },
  ];

  // Freelance admin portal: strip the enterprise-only modules — the whole
  // Vendor category, the standalone Jobs module, and the Manager / Client Pool
  // entries nested under System Values. (Payables live inside the Vendor
  // category, so they disappear with it.)
  const ENTERPRISE_ONLY_LABELS = new Set(["Vendor", "Jobs"]);
  const ENTERPRISE_ONLY_SUBPATHS = new Set(["/managers", "/client-pools"]);

  const freelanceAdminLinks = adminLinks
    .filter((item) => !ENTERPRISE_ONLY_LABELS.has(item.label))
    .map((item) =>
      item.subcategories
        ? {
            ...item,
            subcategories: item.subcategories.filter(
              (sub) => !ENTERPRISE_ONLY_SUBPATHS.has(sub.to)
            ),
          }
        : item
    );

  const vendorManagerLinks = [
    { label: "To Dos", icon: FiCheckSquare, to: "/todos", hasSubcategories: false },
    {
      label: "Vendors", icon: FiUsers, hasSubcategories: true,
      subcategories: [
        { label: "Vendors",      to: "/vendors", end: true    },
        { label: "New Vendor",   to: "/vendors/create-vendor" },
        { label: "Applications", to: "/vendor/applications"   },
        { label: "Contacts",     to: "/vendor/contacts"       },
        { label: "Prices",       to: "/vendor/prices"         },
        { label: "Tags",         to: "/vendor/tags"           },
      ],
    },
    {
      label: "Settings", icon: FiSettings, hasSubcategories: true,
      subcategories: [
        { label: "Account",        to: "/settings/account" },
        { label: "Legal Entities", to: "/settings/legal-entities" },
        {
          label: "Access Control",
          subcategories: [
            { label: "Roles",    to: "/settings/roles" },
            { label: "Managers", to: "/settings/managers" },
          ],
        },
        {
          label: "System Values",
          subcategories: [
            { label: "Language Pairs",  to: "/settings/system-values/language-pairs" },
            { label: "Services",        to: "/settings/system-values/services" },
            { label: "Specialization",  to: "/settings/system-values/specializations" },
            { label: "Units",           to: "/settings/system-values/units" },
            { label: "Currencies",      to: "/settings/system-values/currencies" },
          ],
        },
        { label: "Taxes",         to: "/settings/taxes" },
        { label: "Vendor Portal", to: "/settings/vendor-portal" },
      ],
    },
  ];

  // Project manager portal — a restricted admin nav:
  //  • System Values: full CRUD, but NO Manager / Client Pool / Payment Methods
  //  • Vendor: read-only list + finances (no Create / Applications)
  //  • Client: full CRUD (scoped to the manager's client pool) + finances
  //  • Project & Jobs: full access (scoped to accessible clients)
  const projectManagerLinks = [
    { label: "Dashboard", icon: FiHome, to: "/", hasSubcategories: false },
    {
      label: "System Values", icon: FiBarChart2, to: "/system-values", hasSubcategories: true,
      subcategories: [
        { label: "Services",        to: "/system-values/services" },
        { label: "Language Pairs",  to: "/system-values/language-pairs" },
        { label: "Specializations", to: "/system-values/specializations" },
        { label: "Units",           to: "/system-values/units" },
        { label: "Currencies",      to: "/system-values/currencies" },
      ],
    },
    {
      label: "Vendor", icon: FiUsers, hasSubcategories: true,
      subcategories: [
        { label: "Vendor",   to: "/vendors", end: true },
        { label: "Payables", to: "/vendor/payables" },
        { label: "Invoice",  to: "/vendor/invoices" },
        { label: "Payment",  to: "/vendor/payments" },
      ],
    },
    {
      label: "Client", icon: FiUsers, hasSubcategories: true,
      subcategories: [
        { label: "Client",      to: "/clients", end: true },
        { label: "Create New",  to: "/clients/create-client" },
        { label: "Receivables", to: "/client/receivables" },
        { label: "Invoice",     to: "/client/invoices" },
        { label: "Payment",     to: "/client/payments" },
      ],
    },
    { label: "Project", icon: FiFolder,  to: "/projects", hasSubcategories: false },
    { label: "Jobs",    icon: FiPackage, to: "/jobs",     hasSubcategories: false },
  ];

  const toggle = () => {
    const next = !isOpen;
    setIsOpen(next);
    localStorage.setItem("sidebar-open", next);
    if (next) setFlyout(null);
  };

  const toggleCategory = (label) => {
    setExpandedCategories((prev) => {
      const next = new Set(prev);
      next.has(label) ? next.delete(label) : next.add(label);
      localStorage.setItem("sidebar-expanded", JSON.stringify([...next]));
      return next;
    });
  };

  // Flatten one or more levels of subcategories down to their leaf `to` paths.
  const collectTos = (subs) =>
    subs.flatMap((s) =>
      s.subcategories ? collectTos(s.subcategories) : s.to ? [s.to] : []
    );

  const isSubcategoryActive = (subs) =>
    collectTos(subs).some((to) => location.pathname.startsWith(to));

  // Renders subcategory items, supporting one nested level of groups
  // (a sub that itself has `subcategories`).
  const renderSubItems = (subs) =>
    subs.map((sub) =>
      sub.subcategories ? (
        <div key={sub.label}>
          <button
            type="button"
            onClick={() => toggleCategory(sub.label)}
            className={`flex items-center w-full text-left px-3 py-2 rounded-md text-xs font-medium transition-colors
              ${isSubcategoryActive(sub.subcategories)
                ? "text-blue-400"
                : "text-slate-400 hover:bg-slate-800 hover:text-slate-300"
              }`}
          >
            <span className="flex-1">{sub.label}</span>
            <FiChevronDown
              size={12}
              className={`transition-transform duration-300 ${
                expandedCategories.has(sub.label) ? "rotate-180" : ""
              }`}
            />
          </button>
          {expandedCategories.has(sub.label) && (
            <div className="mt-0.5 ml-3 pl-3 border-l border-slate-700 space-y-0.5">
              {renderSubItems(sub.subcategories)}
            </div>
          )}
        </div>
      ) : (
        <NavLink
          key={sub.to}
          to={sub.to}
          end={sub.end}
          className={({ isActive }) =>
            `flex items-center gap-2 px-3 py-2 rounded-md text-xs font-medium transition-all
             ${isActive
               ? "bg-blue-600 text-white shadow-sm shadow-blue-600/20"
               : "text-slate-500 hover:bg-slate-800 hover:text-slate-300"
             }`
          }
        >
          <span className="w-1 h-1 rounded-full bg-current shrink-0" />
          {sub.label}
        </NavLink>
      )
    );

  /* ── Flyout / tooltip helpers ── */
  const openFlyout = (e, item) => {
    if (isOpen) return;
    clearTimeout(flyoutTimer.current);
    const rect = e.currentTarget.getBoundingClientRect();
    setFlyout({ item, centerY: rect.top + rect.height / 2 });
  };

  const scheduleFlyoutClose = () => {
    flyoutTimer.current = setTimeout(() => setFlyout(null), 150);
  };

  const cancelFlyoutClose = () => clearTimeout(flyoutTimer.current);

  /* ── Shared sidebar shell ── */
  const Shell = ({ children }) => (
    <aside
      className={`fixed left-0 top-16 bottom-0 z-20
        bg-slate-900 border-r border-slate-700/50 shadow-xl
        transition-all duration-300 ease-in-out flex flex-col
        ${isOpen ? "w-64 overflow-y-auto overflow-x-hidden" : "w-[4.5rem] overflow-visible"}`}
    >
      {/* ── Top toggle ── */}
      <div className={`flex shrink-0 border-b border-slate-700/50
        ${isOpen ? "items-center justify-between px-4 py-3" : "items-center justify-center px-2 py-3"}`}
      >
        {isOpen && (
          <span className="text-[10px] font-bold text-slate-500 uppercase tracking-widest select-none">
            Navigation
          </span>
        )}
        <button
          onClick={toggle}
          title={isOpen ? "Collapse sidebar" : "Expand sidebar"}
          className="p-1.5 rounded-lg text-slate-400 hover:bg-slate-800 hover:text-white transition-colors"
        >
          {isOpen
            ? <PanelLeftClose size={18} />
            : <PanelLeftOpen  size={18} />
          }
        </button>
      </div>

      {children}
    </aside>
  );

  /* ── Setup-incomplete ── */
  if (isAdminSetupIncomplete) {
    return (
      <Shell>
        <div className="p-3 space-y-3 mt-1">
          {isOpen && (
            <p className="text-xs text-amber-400 bg-amber-400/10 border border-amber-400/30 rounded-lg px-3 py-2">
              Complete setup to unlock all features.
            </p>
          )}
          <NavLink
            to="/setup"
            className={({ isActive }) =>
              `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all
               ${isActive ? "bg-blue-600 text-white" : "text-slate-400 hover:bg-slate-800 hover:text-slate-200"}`
            }
          >
            <FiSettings size={18} className="shrink-0" />
            {isOpen && <span>Complete Setup</span>}
          </NavLink>
        </div>
      </Shell>
    );
  }

  /* ── Vendor ── */
  if (isVendor) {
    return (
      <Shell>
        <nav className="p-3 space-y-0.5 mt-1">
          {vendorLinks.map((link) => (
            <NavLink
              key={link.to}
              to={link.to}
              onMouseEnter={(e) => openFlyout(e, link)}
              onMouseLeave={scheduleFlyoutClose}
              className={({ isActive }) =>
                `flex items-center rounded-lg transition-all duration-200
                 ${isOpen ? "gap-3 px-3 py-2.5 text-sm font-medium" : "justify-center py-3 px-2"}
                 ${isActive
                   ? "bg-blue-600 text-white shadow-md shadow-blue-600/20"
                   : "text-slate-400 hover:bg-slate-800 hover:text-slate-200"
                 }`
              }
            >
              <link.icon size={18} className="shrink-0" />
              {isOpen && <span>{link.label}</span>}
            </NavLink>
          ))}
        </nav>
      </Shell>
    );
  }

  /* ── Admin / Manager (vendor manager gets a restricted nav, same UI) ── */
  const navLinks = isVendorManager
    ? vendorManagerLinks
    : isProjectManager
    ? projectManagerLinks
    : freelance
    ? freelanceAdminLinks
    : adminLinks;
  return (
    <>
      <Shell>
        <nav className="p-3 space-y-0.5 mt-1 flex-1">
          {navLinks.map((item) => {
            const parentActive = item.hasSubcategories && isSubcategoryActive(item.subcategories);

            /* ── Collapsed view — just icon, no label ── */
            if (!isOpen) {
              const collapsedClasses = `flex items-center justify-center py-3 px-2 rounded-lg transition-all duration-200`;

              if (!item.to) {
                return (
                  <button
                    key={item.label}
                    type="button"
                    onMouseEnter={(e) => openFlyout(e, item)}
                    onMouseLeave={scheduleFlyoutClose}
                    className={`${collapsedClasses} w-full
                      ${parentActive
                        ? "bg-blue-600 text-white shadow-md shadow-blue-600/20"
                        : "text-slate-400 hover:bg-slate-800 hover:text-slate-200"
                      }`}
                  >
                    <item.icon size={18} className="shrink-0" />
                  </button>
                );
              }

              return (
                <NavLink
                  key={item.label}
                  to={item.to}
                  end={item.to === "/"}
                  onMouseEnter={(e) => openFlyout(e, item)}
                  onMouseLeave={scheduleFlyoutClose}
                  onClick={() => setFlyout(null)}
                  className={({ isActive }) =>
                    `${collapsedClasses}
                     ${isActive || parentActive
                       ? "bg-blue-600 text-white shadow-md shadow-blue-600/20"
                       : "text-slate-400 hover:bg-slate-800 hover:text-slate-200"
                     }`
                  }
                >
                  <item.icon size={18} className="shrink-0" />
                </NavLink>
              );
            }

            /* ── Expanded view ── */
            return (
              <div key={item.label}>
                {item.hasSubcategories ? (
                  <>
                    <div
                      className={`flex items-center rounded-lg transition-all duration-200
                        ${parentActive
                          ? "bg-blue-600/15 text-blue-400"
                          : "text-slate-400 hover:bg-slate-800 hover:text-slate-200"
                        }`}
                    >
                      {item.to ? (
                        <>
                          <NavLink
                            to={item.to}
                            onClick={() => {
                              setExpandedCategories((prev) => {
                                const next = new Set(prev);
                                next.delete(item.label);
                                localStorage.setItem("sidebar-expanded", JSON.stringify([...next]));
                                return next;
                              });
                            }}
                            className={({ isActive }) =>
                              `flex items-center gap-3 flex-1 px-3 py-2.5 text-sm font-medium
                               ${isActive || parentActive ? "text-blue-400" : ""}`
                            }
                          >
                            <item.icon size={18} className="shrink-0" />
                            <span>{item.label}</span>
                          </NavLink>
                          <button
                            onClick={() => toggleCategory(item.label)}
                            className="pr-3 py-2.5 pl-1 hover:text-white transition-colors"
                          >
                            <FiChevronDown
                              size={14}
                              className={`transition-transform duration-300 ${
                                expandedCategories.has(item.label) ? "rotate-180" : ""
                              }`}
                            />
                          </button>
                        </>
                      ) : (
                        <button
                          type="button"
                          onClick={() => toggleCategory(item.label)}
                          className={`flex items-center w-full text-left
                            ${parentActive ? "text-blue-400" : ""}`}
                        >
                          <span className="flex items-center gap-3 flex-1 px-3 py-2.5 text-sm font-medium">
                            <item.icon size={18} className="shrink-0" />
                            <span>{item.label}</span>
                          </span>
                          <span className="pr-3 py-2.5 pl-1 transition-colors hover:text-white">
                            <FiChevronDown
                              size={14}
                              className={`transition-transform duration-300 ${
                                expandedCategories.has(item.label) ? "rotate-180" : ""
                              }`}
                            />
                          </span>
                        </button>
                      )}
                    </div>

                    {expandedCategories.has(item.label) && (
                      <div className="mt-0.5 ml-4 pl-3 border-l border-slate-700 space-y-0.5 pb-1">
                        {renderSubItems(item.subcategories)}
                      </div>
                    )}
                  </>
                ) : (
                  <NavLink
                    to={item.to}
                    end={item.to === "/"}
                    className={({ isActive }) =>
                      `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all
                       ${isActive
                         ? "bg-blue-600 text-white shadow-md shadow-blue-600/20"
                         : "text-slate-400 hover:bg-slate-800 hover:text-slate-200"
                       }`
                    }
                  >
                    <item.icon size={18} className="shrink-0" />
                    <span>{item.label}</span>
                  </NavLink>
                )}
              </div>
            );
          })}
        </nav>
      </Shell>

      {/* ── Collapsed flyout / tooltip (portal) ── */}
      {!isOpen && flyout && createPortal(
        <div
          onMouseEnter={cancelFlyoutClose}
          onMouseLeave={scheduleFlyoutClose}
          style={{
            top: flyout.centerY,
            left: COLLAPSED_W + 10,
            transform: "translateY(-50%)",
          }}
          className="fixed z-50"
        >
          {flyout.item.hasSubcategories ? (
            /* Subcategory dropdown */
            <div className="w-52 bg-slate-800 border border-slate-700 rounded-xl shadow-2xl py-1.5 overflow-hidden">
              <div className="flex items-center gap-2 px-3 py-2 border-b border-slate-700/60 mb-1">
                <flyout.item.icon size={13} className="text-blue-400 shrink-0" />
                <span className="text-[11px] font-bold text-slate-300 uppercase tracking-wider">
                  {flyout.item.label}
                </span>
              </div>
              {flyout.item.subcategories.map((sub) => {
                if (sub.subcategories) {
                  return (
                    <div key={sub.label}>
                      <div className="px-4 pt-2 pb-1 text-[10px] font-bold uppercase tracking-wider text-slate-500">
                        {sub.label}
                      </div>
                      {sub.subcategories.map((child) => {
                        const isActive = child.end
                          ? location.pathname === child.to
                          : location.pathname.startsWith(child.to);
                        return (
                          <NavLink
                            key={child.to}
                            to={child.to}
                            onClick={() => setFlyout(null)}
                            className={`flex items-center gap-2.5 pl-7 pr-4 py-2 text-sm transition-colors
                              ${isActive
                                ? "bg-blue-600 text-white"
                                : "text-slate-400 hover:bg-slate-700 hover:text-slate-200"
                              }`}
                          >
                            <span className={`w-1.5 h-1.5 rounded-full shrink-0 ${isActive ? "bg-white" : "bg-slate-600"}`} />
                            {child.label}
                          </NavLink>
                        );
                      })}
                    </div>
                  );
                }
                const isActive = sub.end
                  ? location.pathname === sub.to
                  : location.pathname.startsWith(sub.to);
                return (
                  <NavLink
                    key={sub.to}
                    to={sub.to}
                    onClick={() => setFlyout(null)}
                    className={`flex items-center gap-2.5 px-4 py-2 text-sm transition-colors
                      ${isActive
                        ? "bg-blue-600 text-white"
                        : "text-slate-400 hover:bg-slate-700 hover:text-slate-200"
                      }`}
                  >
                    <span className={`w-1.5 h-1.5 rounded-full shrink-0 ${isActive ? "bg-white" : "bg-slate-600"}`} />
                    {sub.label}
                  </NavLink>
                );
              })}
            </div>
          ) : (
            /* Simple label tooltip */
            <div className="flex items-center">
              {/* arrow */}
              <div className="w-0 h-0 border-t-[6px] border-t-transparent border-b-[6px] border-b-transparent border-r-[7px] border-r-white" />
              <div className="bg-white text-slate-800 text-sm font-semibold px-3 py-1.5 rounded-lg shadow-lg whitespace-nowrap">
                {flyout.item.label}
              </div>
            </div>
          )}
        </div>,
        document.body
      )}
    </>
  );
};

export default Sidebar;
